import { scrollable } from "@/common";
import styled from "@emotion/styled";
import Icon from "@imminently/decisively-ui/Icon";
import Button from "@imminently/decisively-ui/Button";
import { Chip, Typography } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { formatDistanceToNow } from "date-fns";
import React from "react";

//# region TablePaginationActions

const TablePaginationActionsWrap = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
`;
const RangeTypography = styled(Typography)`
  white-space: nowrap;
`;
type TablePaginationActionsProps = {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (e: unknown, page: number) => unknown;
};
const TablePaginationActions: React.FC<TablePaginationActionsProps> = (props) => {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 1);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage)));
  };

  const range = `${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, count)}`;

  return (
    <TablePaginationActionsWrap>
      <Button icon
        onClick={handleFirstPageButtonClick}
        disabled={page === 1}
        label="first page"
      >
        <Icon name="go-back" />
      </Button>
      <Button icon
        onClick={handleBackButtonClick}
        disabled={page === 1}
        label="previous page"
      >
        <Icon name="go-back" />
      </Button>
      <RangeTypography>
        {range} of {count}
      </RangeTypography>
      <Button icon
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage)}
        label="Next page"
      >
        <Icon name="go-forward" />
      </Button>
      <Button icon
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage)}
        label="Last page"
      >
        <Icon name="go-forward" />
      </Button>
    </TablePaginationActionsWrap>
  );
};

//# endregion

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 25%;
  flex-shrink: 0;
  flex-grow: 0;
  margin: 0;
  overflow: auto;
  gap: 0.5rem;
  padding-top: 0;
  padding-bottom: 0;
`;

const ChangesWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

/**
 * typecast here is kinda safe as TimelineItem will pass any props\
 * to the native element underneath (which is a div)
 */
const StyledTimelineItem = styled.div`
  display: flex;
  flex-direction: row-reverse;
  gap: 0.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
  padding: 0.5rem 0.25rem;

  &:hover {
    background-color: rgba(155,155,155,0.15);
  }
`;

const StyledTimelineContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: calc(100% - 1rem);
  flex-grow: 1;
`;
const TimelineSeparator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const TimelineDot = styled.div`
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  background-color: rgba(117,117,117,0.9);
  margin-top: 0.375rem;
`;
const TimelineConnector = styled.div`
  width: 2px;
  min-height: 1.75rem;
  background-color: rgba(117,117,117,0.4);
`;
const TimelineContentFirstLine = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;
const TimelineContentFirstLineName = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledTablePagination = styled(TablePagination)`
  border: none;

  .MuiToolbar-root { min-height: auto; }

  .MuiTablePagination-caption { display: none; }
`;

export type ChangeHistoryTimelineProps = {
  changes: { pageData: any[] };
  setCurrentChange: (v: unknown) => unknown;
  pagination: { page: number; perPage: number };
  count: number;
  setCurrentPage: (num: number) => unknown;
};

export const ChangeHistoryTimeline: React.FC<ChangeHistoryTimelineProps> = React.memo((p) => {
  const { changes, setCurrentChange, pagination, count, setCurrentPage } = p;

  return (
    <Wrap>
      <ChangesWrap className={scrollable}>
        {changes.pageData.map((change) => (
          <StyledTimelineItem
            key={change.id}
            onClick={() => void setCurrentChange(change)}
          >
            <StyledTimelineContent>
              <TimelineContentFirstLine>
                <Chip label={`${change.what.length} change${change.what.length > 1 ? "s" : ""}`} />

                <TimelineContentFirstLineName
                  style={{ fontWeight: 400, fontSize: 14 }}
                  title={change.who.name}
                >
                  by {change.who.name}
                </TimelineContentFirstLineName>
              </TimelineContentFirstLine>

              <Typography
                variant="caption"
                style={{ fontWeight: 400, marginLeft: "0.5rem" }}
              >
                {formatDistanceToNow(new Date(change.when), { addSuffix: true })}
              </Typography>
            </StyledTimelineContent>

            <TimelineSeparator>
              <TimelineDot />
              <TimelineConnector />
            </TimelineSeparator>
          </StyledTimelineItem>
        ))}
      </ChangesWrap>

      <StyledTablePagination
        style={{ flexShrink: 0 }}
        rowsPerPageOptions={[10]}
        count={count}
        rowsPerPage={pagination?.perPage}
        page={pagination?.page}
        onPageChange={(_, p) => setCurrentPage(p)}
        // @ts-ignore
        ActionsComponent={TablePaginationActions}
      />
    </Wrap>
  );
});
ChangeHistoryTimeline.displayName = "components/ChangeHistory/Timeline";
